/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - European Union 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [sidebar.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 26.09.18 20:49
 */

import {useDispatch} from 'react-redux';
import React, {Fragment, useEffect} from 'react';

import {asyncReorderSpaceRanking, EVENT_SPACE, GENERIC_SPACE,} from "../../actions/spaces";
import Widget from '../widgets/widget';
import {useAuthorization, useSiteConfiguration} from "../../selectors";
import {SidebarHeadline} from "../navigation/links/nav-headlines";
import {
    isCalendarDateWithinInterval,
    isDateWithinInterval,
    isISODateAfter,
    useLanguage
} from "../configuration/configuration";
import SortableContainer, {SortableEntry} from "../sortable/sortable-container";
import {FETCH_ALL_SPACES, FETCH_EVENTS_ONLY, FETCH_SPACES_ONLY} from "../hooks/async-fetch-generic-spaces";
import {notify} from "../notifications/notify";
import SidebarSimpleEntryEvent from "./lists/sidebar-simple-entry-event";
import {SUCCESS_STATUS} from "../../actions";
import {Spinner} from "../util/spinner";
import {ErrorBoundary} from "../error-boundary/ErrorBoundary";
import usePeekGenericSpaces from "../hooks/use-peek-generic-spaces";
import usePeekWidgets from "../hooks/use-peek-widgets";
import usePeekEventSpaces from "../hooks/use-peek-event-spaces";
import {getPublicUser} from "../../actions/environment";
import SidebarEntryPeekSpace from "./lists/sidebar-entry-peekspace";
import CalendarViewEntryPeekEvent from "../../views/calendar-view/calendar-view-entry-peekevent";

const Sidebar = ({username, location, fetch = FETCH_ALL_SPACES}) => {
    const {authorization, isTransitioning, principalUsername, isAuthorized} = useAuthorization();
    const {homePage, siteUsername} = useSiteConfiguration();
    const {peekGenericSpaces, spaces, status: spacesStatus} = usePeekGenericSpaces();
    const {peekEventSpaces, events, status: eventsStatus} = usePeekEventSpaces();
    const {peekWidgets, widgets, status: widgetsStatus} = usePeekWidgets();

    const dispatch = useDispatch();
    const Lang = useLanguage();

    useEffect(() => {
        const {fetchname, isParent} = resolveUsernameByLocation(location);
        isRenderSpaces && peekGenericSpaces(fetchname, isParent);
        isRenderEvents && peekEventSpaces(fetchname, isParent);
        // isRenderWidgets && peekWidgets();
    }, [username, location]);

    const resolveUsernameByLocation = (location) => {
        if (isAuthorized) {
            // superuser
            if (location.pathname === homePage) {
                return {fetchname: siteUsername, isParent: true}

                // logged-in user home space
            } else if (location.pathname === `/${principalUsername}/home`) {
                return {fetchname: principalUsername, isParent: false}

                // smthg.else
            } else return {fetchname: siteUsername, isParent: true}
        }
        return {fetchname: getPublicUser(), isParent: true}
    }

    const renderGenericSpaces = () => {
        if (!spaces || !Array.isArray(spaces)) {
            console.log('Spaces is null or is not an array. System error.', spaces);
            return null;
        }
        try {
            const genericSpaces = spaces
            .filter(space => space.type === GENERIC_SPACE)
            .map(space => {
                return <SortableEntry list key={space.id} className='list-unstyled mb-1'
                                      position={space.ranking}
                                      data={space.id}>
                    <SidebarEntryPeekSpace peekspace={space} size='medium'/>
                </SortableEntry>
            })
            return genericSpaces.length > 0 ? genericSpaces :
                <div className='mb-2'>{Lang.nav.sidebar.spaces.noneUpcoming}</div>;

        } catch (error) {
            console.error('Sidebar', error);
            console.error('Spaces', spaces);
            return <div className='mb-2'>{Lang.nav.sidebar.spaces.noneUpcoming} (Error)</div>;
        }
    }

    const renderTodayEvents = () => {
        const today = new Date();
        const todayEvents = events
            .filter(event => isCalendarDateWithinInterval(today,
                    event.calendarDate,
                    event.calendarDateEnd))
            .map(event => {
                return <li key={event.id} className='list-unstyled'>
                    <CalendarViewEntryPeekEvent peekspace={event}/>
                </li>
            })
        return todayEvents.length > 0 ? todayEvents :
            <div className='mb-2'>{Lang.nav.sidebar.events.noneToday}</div>;
    }

    // renders only events in current month and after based on first entry in calendarDates, no sorting for events
    const renderUpcomingEvents = () => {
        // const pastMonth = getLastDayOfPastMonth();
        const today = new Date();

        const calendarEvents = events
        .filter(space => {
            if (space.calendarDate) {
                // const isAfter = isISODateAfter(space.calendarDate.date, pastMonth);
                const isAfter = isISODateAfter(space.calendarDate.date, today);
                return isAfter && (space.type === EVENT_SPACE);
            } else return false;
        })
        .map(space => {
            return <li key={space.id} className='list-unstyled'>
                <SidebarSimpleEntryEvent peekspace={space}/>
            </li>
        })
        return calendarEvents.length > 0 ? calendarEvents :
            <div className='mb-2'>{Lang.nav.sidebar.events.noneUpcoming}</div>;
    }

    const renderTopWidgets = () => {
        if (!widgets) return null;
        return widgets.filter(widget => widget.pos === 'RTOP').map(widget => {
            return <Widget key={widget.id} widget={widget} authname={principalUsername}
                           authorization={authorization} mode='RIGHT'/>
        })
    }

    const renderBottomWidgets = () => {
        if (!widgets) return '';
        return widgets.filter(widget => widget.pos === 'RBOTTOM').map(widget => {
            return <Widget key={widget.id} widget={widget} authname={principalUsername}
                           authorization={authorization} mode='RIGHT'/>
        })
    }

    const reorderRanking = (evt) => {
        const ranks = [];
        const maxRanking = evt.to.children.length;
        [...evt.to.children].forEach((child, idx) => {
            child.dataset.space && ranks.push({spaceId: child.dataset.space, ranking: maxRanking - idx});
        });

        dispatch(asyncReorderSpaceRanking(principalUsername, {ranks: ranks}, spaces => {
                notify(`${spaces.length} spaces ranking set successfully`);
            })
        );
    };

    const renderFallBack = () => {
        return <div className='mb-2'>
            {Lang.nav.sidebar.spaces.noneUpcoming}
            <p>(Something went wrong)</p>
        </div>;
    }

    const isRenderWidgets = false;
    const isRenderEvents = fetch === FETCH_ALL_SPACES || fetch === FETCH_EVENTS_ONLY;
    const isRenderSpaces = fetch === FETCH_ALL_SPACES || fetch === FETCH_SPACES_ONLY;
    const isPending =
        (isRenderSpaces && spacesStatus !== SUCCESS_STATUS) ||
        (isRenderEvents && eventsStatus !== SUCCESS_STATUS) ||
        (isRenderWidgets && widgetsStatus !== SUCCESS_STATUS);

    if (isPending || isTransitioning) return <Spinner/>;

    const isSiteUserCalendar = siteUsername === username;

    return <ErrorBoundary fallback={renderFallBack}>
        <div className='sidebar-container mb-3'>
            {isRenderWidgets && <Fragment>
                <div className="widget-container">
                    {renderTopWidgets()}
                </div>
            </Fragment>}

            {isRenderEvents && <Fragment>
                <SidebarHeadline border={false} title={Lang.nav.sidebar.events.today}/>
                {events &&
                    <ul className='list-group'>
                        {renderTodayEvents()}
                    </ul>}
            </Fragment>}

            {/*{isRenderEvents && <Fragment>*/}
            {/*    <SidebarHeadline border={false}*/}
            {/*                     title={isSiteUserCalendar ? Lang.nav.sidebar.events.current : Lang.nav.sidebar.events.yours}/>*/}
            {/*    {events &&*/}
            {/*        <ul className='list-group'>*/}
            {/*            {renderUpcomingEvents()}*/}
            {/*        </ul>}*/}
            {/*</Fragment>}*/}
        </div>

        <div className='sidebar-container'>
            {isRenderSpaces && <Fragment>
                <SidebarHeadline border={false}
                                 title={isSiteUserCalendar ? Lang.nav.sidebar.spaces.current : Lang.nav.sidebar.spaces.yours}/>
                {spaces && <SortableContainer list className='list-group' onSorted={reorderRanking}>
                    {renderGenericSpaces()}
                </SortableContainer>}
            </Fragment>}

            {isRenderWidgets && <Fragment>
                <div className="widget-container pt-4">
                    {renderBottomWidgets()}
                </div>
            </Fragment>}
        </div>
    </ErrorBoundary>
}

export default React.memo(Sidebar);